import React, { createContext, useContext, useEffect, useState } from 'react'

interface UserInteractionContextType {
  hasInteracted: boolean
  handleInteraction: () => void
}

const UserInteractionContext = createContext<UserInteractionContextType | undefined>(undefined)

export const UserInteractionProvider = ({ children }: { children: React.ReactNode }) => {
  const [hasInteracted, setHasInteracted] = useState(false)

  const handleInteraction = () => {
    setHasInteracted(true)
  }

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    const handleClick = () => {
      handleInteraction()
    }

    document.addEventListener('click', handleClick)

    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [handleInteraction])

  return (
    <UserInteractionContext.Provider value={{ hasInteracted, handleInteraction }}>
      {children}
    </UserInteractionContext.Provider>
  )
}

export const useUserInteraction = (): UserInteractionContextType => {
  const context = useContext(UserInteractionContext)
  
  // During SSR, return a default context to prevent errors
  if (typeof window === 'undefined') {
    return {
      hasInteracted: false,
      handleInteraction: () => {},
    }
  }
  
  if (!context) {
    throw new Error('useUserInteraction must be used within a UserInteractionProvider')
  }
  return context
}
